'use client';

import { Box, Button, Typography } from '@mui/material';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import type { BuzzBridgeJobListItem } from '../utils/mapBuzzBridgeJob';
import { CampaignThreadUnreadBadge } from '../CampaignThreadUnreadBadge';
import CustomLazyLoadImage from '../../Image/CustomLazyLoadImage';

const SURFACE_CONTAINER = '#1a1919';
const SURFACE_HI = '#262626';
const GOLD = '#ffc965';
const MINT = '#00fdc1';
const ON_PRIMARY = '#392700';
const ON_VARIANT = '#adaaaa';
const ERROR = '#ff7351';

interface WorkManagementJobListProps {
    jobs: BuzzBridgeJobListItem[];
    onOpenWorkspace: (ctx: { campaignId: string; participantId: string }) => void;
}

function initials(name: string): string {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return '•';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
}

function actionLabel(job: BuzzBridgeJobListItem): string {
    if (job.status === 'completed') return 'Workspace';
    if (job.status === 'pending') return 'View Submission';
    return 'Open Studio Workspace';
}

export function WorkManagementJobList({ jobs, onOpenWorkspace }: WorkManagementJobListProps) {
    return (
        <Box
            component="section"
            sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, width: '100%', minWidth: 0 }}>
            {jobs.map(job => {
                const pending = job.status === 'pending';
                const completed = job.status === 'completed';
                const open = () => onOpenWorkspace({ campaignId: job.campaignId, participantId: job.participantId });
                const due = job.deadline && job.deadline !== '—' ? job.deadline : null;
                const chipBg = completed ? '#262626' : pending ? 'rgba(255,201,101,0.1)' : 'rgba(0,253,193,0.1)';
                const chipColor = completed ? '#fff' : pending ? GOLD : MINT;

                return (
                    <Box
                        key={job.id}
                        sx={{
                            p: 2.5,
                            borderRadius: 1.5,
                            bgcolor: SURFACE_CONTAINER,
                            display: 'flex',
                            flexDirection: { xs: 'column', md: 'row' },
                            alignItems: { xs: 'stretch', md: 'center' },
                            justifyContent: 'space-between',
                            gap: 2,
                            transition: 'background-color 0.2s',
                            '&:hover': { bgcolor: '#201f1f' },
                        }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, minWidth: 0, flex: 1 }}>
                            <Box
                                sx={{
                                    width: 48,
                                    height: 48,
                                    borderRadius: 1.5,
                                    overflow: 'hidden',
                                    flexShrink: 0,
                                    bgcolor: SURFACE_HI,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}>
                                {job.logoUrl ? (
                                    <CustomLazyLoadImage
                                        src={job.logoUrl}
                                        alt=""
                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                    />
                                ) : (
                                    <Typography
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            fontWeight: 900,
                                            fontSize: 14,
                                            color: GOLD,
                                        }}>
                                        {initials(job.company || job.title)}
                                    </Typography>
                                )}
                            </Box>
                            <Box sx={{ minWidth: 0 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                    {job.company ? (
                                        <Typography
                                            sx={{
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                fontSize: 12,
                                                letterSpacing: '0.08em',
                                                textTransform: 'uppercase',
                                                color: ON_VARIANT,
                                            }}>
                                            {job.company}
                                        </Typography>
                                    ) : null}
                                    <Box
                                        sx={{
                                            px: 1,
                                            py: 0.25,
                                            borderRadius: 999,
                                            bgcolor: chipBg,
                                            color: chipColor,
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            fontSize: 10,
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 0.5,
                                        }}>
                                        {!pending && !completed ? (
                                            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: MINT }} />
                                        ) : null}
                                        {job.statusLabel}
                                    </Box>
                                    {due ? (
                                        <Box
                                            sx={{
                                                px: 1,
                                                py: 0.25,
                                                borderRadius: 999,
                                                bgcolor: 'rgba(255,115,81,0.1)',
                                                color: ERROR,
                                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                                fontSize: 10,
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: 0.5,
                                            }}>
                                            <ScheduleIcon sx={{ fontSize: 12 }} />
                                            Due {due}
                                        </Box>
                                    ) : null}
                                    <CampaignThreadUnreadBadge count={job.unreadMessageCount} size="sm" />
                                </Box>
                                <Typography
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        fontWeight: 700,
                                        fontSize: 16,
                                        color: '#fff',
                                        mt: 0.5,
                                    }}>
                                    {job.title}
                                </Typography>
                                {job.description ? (
                                    <Typography
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            fontSize: 12,
                                            color: ON_VARIANT,
                                            mt: 0.25,
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                        }}>
                                        {job.description}
                                    </Typography>
                                ) : null}
                            </Box>
                        </Box>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 2.5,
                                flexShrink: 0,
                                pt: { xs: 1.5, md: 0 },
                                borderTop: { xs: `1px solid rgba(72,72,71,0.45)`, md: 'none' },
                            }}>
                            <Box sx={{ textAlign: { xs: 'left', md: 'right' } }}>
                                <Typography
                                    sx={{
                                        fontSize: 10,
                                        letterSpacing: '0.12em',
                                        textTransform: 'uppercase',
                                        color: ON_VARIANT,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    }}>
                                    Contract Payout
                                </Typography>
                                <Typography
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        fontWeight: 800,
                                        fontSize: 16,
                                        color: completed ? '#fff' : GOLD,
                                    }}>
                                    {job.rewardDisplay}
                                </Typography>
                            </Box>
                            <Button
                                onClick={open}
                                sx={{
                                    px: 2,
                                    py: 1,
                                    borderRadius: 1.5,
                                    textTransform: 'none',
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    fontWeight: 700,
                                    fontSize: 12,
                                    whiteSpace: 'nowrap',
                                    ...(pending || completed
                                        ? {
                                              bgcolor: SURFACE_HI,
                                              color: '#fff',
                                              '&:hover': { bgcolor: '#2c2c2c' },
                                          }
                                        : {
                                              background: `linear-gradient(90deg, ${GOLD}, #feb700)`,
                                              color: ON_PRIMARY,
                                              '&:hover': { filter: 'brightness(1.08)' },
                                          }),
                                }}>
                                {actionLabel(job)}
                            </Button>
                        </Box>
                    </Box>
                );
            })}
        </Box>
    );
}
